/**
 * Shared interface parts.
 *
 * Every status uses a word beside its color, so color alone never carries
 * meaning. Every action label states the action.
 */

import { isValidElement, type ReactNode } from "react";

import type { Finding, ObjectiveConclusion, WorkingState } from "../lib/api.ts";
import type { NoteVisibility } from "../lib/types.ts";
import { LoadingMark } from "./space/motion.tsx";
import { Icon, type IconName } from "./icon.tsx";
import type { ActionIconName } from "./icon-names.ts";

/**
 * The colour names of `tokens.css`, as Tailwind reads them (`styles.css`).
 * A class like `text-muted` is a colour; `text-sm` is a size.
 */
const TOKEN_COLOURS =
  "ink|accent|accent-dark|highlight|highlight-tint|highlight-dark|canvas|paper|muted|muted-light|" +
  "divider|input-border|success|success-tint|attention|caution|error|error-dark|error-tint|info|" +
  "selected|grid|on-fill|transparent|current";

/** The property a utility class sets, for the classes that commonly override one another. */
const CONFLICT_GROUPS: readonly (readonly [string, RegExp])[] = [
  ["width", /^w-/],
  ["min-width", /^min-w-/],
  ["max-width", /^max-w-/],
  ["height", /^h-/],
  ["min-height", /^min-h-/],
  ["padding", /^p-/],
  ["padding-x", /^px-/],
  ["padding-y", /^py-/],
  ["padding-left", /^pl-/],
  ["padding-right", /^pr-/],
  ["margin-top", /^-?mt-/],
  ["margin-left", /^-?ml-/],
  ["justify", /^justify-/],
  ["align-items", /^items-/],
  ["gap", /^gap-/],
  ["display", /^(?:block|inline-block|inline|flex|inline-flex|grid|hidden|table-cell)$/],
  ["font-weight", /^font-(?:normal|medium|semibold|bold)$/],
  ["font-size", /^text-(?:xs|sm|base|lg|xl|2xl|3xl)$/],
  ["text-align", /^text-(?:left|center|right)$/],
  ["text-colour", new RegExp(`^text-(?:${TOKEN_COLOURS})(?:/\\d+)?$`)],
  ["background", new RegExp(`^bg-(?:${TOKEN_COLOURS})(?:/\\d+)?$`)],
  ["border-colour", new RegExp(`^border-(?:${TOKEN_COLOURS})$`)],
  ["radius", /^rounded(?:-(?:arc|none|full|sm|md|lg))?$/],
  ["line-height", /^leading-/],
];

/**
 * Join classes, and let a later class win over an earlier one that sets the
 * same property.
 *
 * Pages adjust a shared look by adding to it, such as
 * `classes(inputClass, "w-auto py-1")`. In Tailwind both classes stay in the
 * document and the stylesheet order decides, so the adjustment can lose.
 * Here the earlier class of the same property, with the same variant prefix
 * (`hover:`, `tablet:`), is dropped. Classes outside the groups are kept.
 */
export function classes(...values: (string | false | null | undefined)[]): string {
  const tokens = values.filter(Boolean).join(" ").split(/\s+/).filter((token) => token !== "");
  const seen = new Map<string, number>();
  const keep = tokens.map(() => true);
  tokens.forEach((token, index) => {
    const cut = token.lastIndexOf(":");
    const variant = cut === -1 ? "" : token.slice(0, cut + 1);
    const utility = cut === -1 ? token : token.slice(cut + 1);
    const group = CONFLICT_GROUPS.find(([, pattern]) => pattern.test(utility))?.[0];
    if (group === undefined) return;
    const key = variant + group;
    const earlier = seen.get(key);
    if (earlier !== undefined) keep[earlier] = false;
    seen.set(key, index);
  });
  return tokens.filter((_, index) => keep[index]).join(" ");
}

// --- Buttons -------------------------------------------------------------

/**
 * What a button means. `primary` (the highlight) is the one action that
 * creates what the page is about. `main` (the accent) is the action inside a
 * form or a panel. `secondary` is everything else, `quiet` should not draw the
 * eye, `danger` removes something, and `link` is an action that reads as a
 * word inside a sentence. See docs/UI_STYLE.md.
 */
export type ButtonVariant = "main" | "primary" | "secondary" | "quiet" | "danger" | "link";
export type ButtonSize = "normal" | "small";

const BUTTON_BASE =
  "inline-flex items-center justify-center gap-2 rounded-arc font-medium " +
  "transition-colors disabled:cursor-not-allowed disabled:opacity-50";

// A thumb's target is at least 44 pixels (docs/MOBILE.md). The small size is
// for a table row on a wide screen.
const BUTTON_SIZE: Record<ButtonSize, string> = {
  normal: "min-h-11 px-4 py-2",
  small: "px-3 py-1 text-sm",
};

const BUTTON_VARIANT: Record<Exclude<ButtonVariant, "link">, string> = {
  main: "bg-accent text-on-fill hover:bg-accent-dark",
  primary: "bg-highlight text-on-fill hover:bg-highlight-dark",
  secondary: "border border-input-border bg-paper text-ink hover:bg-selected",
  quiet: "text-accent hover:bg-selected",
  danger: "bg-error text-on-fill hover:bg-error-dark",
};

const LINK_VARIANT =
  "inline-flex items-center gap-1 font-medium text-accent underline underline-offset-2 " +
  "hover:text-accent-dark disabled:cursor-not-allowed disabled:opacity-50";

/**
 * The classes of a button. **The one place a button's look is written.** The
 * `Button` component, every link that acts as a button, and every file input
 * read it, so a change here changes all of them.
 */
export function buttonClass(variant: ButtonVariant = "secondary", size: ButtonSize = "normal"): string {
  if (variant === "link") return classes(LINK_VARIANT, size === "small" && "text-sm");
  return classes(BUTTON_BASE, BUTTON_SIZE[size], BUTTON_VARIANT[variant]);
}

/**
 * The icon of an action, chosen by the first word of its label.
 *
 * Action labels start with their verb (docs/INTERFACE_WRITING.md), so the
 * verb decides the mark: every *Add* has the same plus, every *Delete* the
 * same bin. **The one place that pairs a verb with an icon.** Verbs that are
 * not here get no icon, on purpose: Save, Cancel, Continue and Close are the
 * words of every form, and a mark on each would be noise.
 */
const VERB_ICON: Record<string, ActionIconName> = {
  add: "plus",
  new: "plus",
  create: "plus",
  invite: "send",
  send: "send",
  resend: "send",
  export: "download",
  download: "download",
  upload: "upload",
  import: "import",
  delete: "trash",
  remove: "trash",
  edit: "edit",
  rename: "edit",
  approve: "approve",
  accept: "approve",
  filter: "filter",
  filters: "filter",
  refresh: "refresh",
  sync: "refresh",
  copy: "copy",
  print: "print",
  restore: "undo",
  undo: "undo",
  archive: "archive",
};

export function actionIcon(label: ReactNode): ActionIconName | null {
  if (typeof label !== "string") return null;
  const verb = label.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, "") ?? "";
  return VERB_ICON[verb] ?? null;
}

interface ButtonProps {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly type?: "button" | "submit";
  readonly variant?: ButtonVariant;
  readonly disabled?: boolean;
  readonly title?: string;
  readonly size?: ButtonSize;
  /**
   * The leading icon. Left out, the label's verb chooses one (`actionIcon`);
   * `null` draws none.
   */
  readonly icon?: IconName | null;
  readonly ariaLabel?: string;
  readonly ariaExpanded?: boolean;
  readonly ariaPressed?: boolean;
  readonly testId?: string;
}

export function Button({
  children,
  onClick,
  type = "button",
  variant = "secondary",
  disabled = false,
  title,
  size = "normal",
  icon,
  ariaLabel,
  ariaExpanded,
  ariaPressed,
  testId,
}: ButtonProps) {
  const mark = icon === undefined ? actionIcon(children) : icon;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-pressed={ariaPressed}
      data-testid={testId}
      className={buttonClass(variant, size)}
    >
      {mark !== null && variant !== "link" && <Icon name={mark} size="0.9em" />}
      {children}
    </button>
  );
}

/**
 * A button that is only an icon: close, remove, more actions.
 *
 * The label is its accessible name and its tooltip, so it is never a mark
 * alone. The normal size is a 44-pixel square for a thumb; the small size is
 * for a chip or a dense row on a wide screen.
 */
export function IconButton({
  icon,
  label,
  onClick,
  size = "normal",
  tone = "muted",
  disabled = false,
  ariaExpanded,
  ariaHasPopup,
  testId,
}: {
  readonly icon: IconName;
  readonly label: string;
  readonly onClick?: () => void;
  readonly size?: ButtonSize;
  readonly tone?: "muted" | "danger";
  readonly disabled?: boolean;
  readonly ariaExpanded?: boolean;
  readonly ariaHasPopup?: "menu" | "dialog";
  readonly testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHasPopup}
      data-testid={testId}
      className={classes(
        "inline-flex shrink-0 items-center justify-center rounded-arc transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        size === "small" ? "h-6 w-6 text-xs" : "h-11 w-11",
        tone === "danger" ? "text-muted hover:bg-error-tint hover:text-error" : "text-muted hover:bg-selected hover:text-ink",
      )}
    >
      <Icon name={icon} size={size === "small" ? "0.75rem" : "1rem"} />
    </button>
  );
}

/**
 * A link that does the work of a button.
 *
 * Some actions are addresses: "Create SSP" opens a page, "Export" fetches a
 * file. A link that acts like a button is drawn like one — and it stays an
 * anchor, so the middle button of a mouse, a long press and a screen reader
 * all keep the behaviour a link is supposed to have. These are `buttonClass`
 * under the names the pages already use.
 */
export const LINK_BUTTON_CLASS = buttonClass("secondary");

/** The same, in the size a table row uses. */
export const LINK_BUTTON_SMALL_CLASS = buttonClass("secondary", "small");

/** A link that acts like a quiet button: no border, the accent colour. */
export const LINK_QUIET_CLASS = buttonClass("quiet", "small");

/** The leading icon of a link that acts as a button, by the same verb rule. */
export function ActionMark({ label, icon }: { readonly label: ReactNode; readonly icon?: IconName | null }) {
  const mark = icon === undefined ? actionIcon(label) : icon;
  return mark === null ? null : <Icon name={mark} size="0.9em" />;
}

// --- Page header ----------------------------------------------------------

/**
 * The title of a page, with its actions on the right.
 *
 * **The one place a page title is drawn.** Pages wrote their own `h1` in two
 * sizes; this is the one size. The actions wrap under the title on a narrow
 * screen. `children` is a line under the title, such as what the page is for.
 */
export function PageHeader({
  title,
  actions,
  children,
}: {
  readonly title: ReactNode;
  readonly actions?: ReactNode;
  readonly children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{title}</h1>
        {actions !== undefined && <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children !== undefined && children !== null && children !== false && children !== "" && (
        <div className="text-sm text-muted">{children}</div>
      )}
    </div>
  );
}

/**
 * A short value that can be taken away, such as a role on a person.
 *
 * It looks like a quiet badge, and its remove control is a small icon button
 * whose label names the value.
 */
export function Chip({
  children,
  onRemove,
  removeLabel,
}: {
  readonly children: ReactNode;
  readonly onRemove?: () => void;
  readonly removeLabel?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1 rounded-arc bg-selected px-2 py-0.5 text-sm">
      {children}
      {onRemove !== undefined && (
        <IconButton icon="close" size="small" tone="danger" label={removeLabel ?? "Remove"} onClick={onRemove} />
      )}
    </span>
  );
}

// --- Status labels --------------------------------------------------------

const FINDING_LABEL: Record<Finding, string> = {
  MET: "Met",
  NOT_MET: "Not met",
  "N/A": "Not applicable",
  UNASSESSED: "Not assessed",
};

const FINDING_STYLE: Record<Finding, string> = {
  MET: "bg-success text-on-fill",
  NOT_MET: "bg-error text-on-fill",
  "N/A": "bg-info text-on-fill",
  UNASSESSED: "border border-input-border bg-paper text-muted",
};

/** Show the official assessment finding. */
export function FindingLabel({ value }: { readonly value: Finding }) {
  return (
    <span
      className={classes(
        "inline-block rounded-arc px-2 py-0.5 text-sm font-medium",
        FINDING_STYLE[value],
      )}
    >
      {FINDING_LABEL[value]}
    </span>
  );
}

const WORKING_LABEL: Record<WorkingState, string> = {
  not_started: "Not started",
  in_progress: "In progress",
  ready_for_review: "Ready for review",
  reviewed: "Reviewed",
};

/** Show the working state. It is not an assessment finding. */
export function WorkingLabel({ value }: { readonly value: WorkingState }) {
  return (
    <span className="inline-block rounded-arc border border-divider bg-canvas px-2 py-0.5 text-sm text-muted">
      {WORKING_LABEL[value]}
    </span>
  );
}

export type BadgeTone = "neutral" | "good" | "bad" | "warn" | "caution" | "info" | "quiet";

const BADGE_TONE: Record<BadgeTone, string> = {
  neutral: "border border-divider bg-canvas text-muted",
  good: "bg-success text-on-fill",
  bad: "bg-error text-on-fill",
  warn: "bg-attention text-on-fill",
  // Yellow: work in progress. Yellow is light, so its text is the ink colour.
  caution: "bg-caution text-ink",
  info: "bg-info text-on-fill",
  quiet: "border border-input-border bg-paper text-muted",
};

/** A short status word with the color of its tone. */
export function Badge({ children, tone = "neutral" }: { readonly children: ReactNode; readonly tone?: BadgeTone }) {
  return <span className={classes("inline-block rounded-arc px-2 py-0.5 text-sm font-medium", BADGE_TONE[tone])}>{children}</span>;
}

/** The visibility of a note, as a word beside its color. */
export function VisibilityBadge({ visibility }: { readonly visibility: NoteVisibility }) {
  if (visibility === "shared") return <Badge tone="info">Shared with assessors</Badge>;
  if (visibility === "assessor_private") return <Badge tone="neutral">Assessor private</Badge>;
  return <Badge tone="neutral">Internal</Badge>;
}

const CONCLUSION_LABEL: Record<ObjectiveConclusion, string> = {
  SATISFIED: "Satisfied",
  NOT_SATISFIED: "Not satisfied",
  NOT_APPLICABLE: "Not applicable",
  UNASSESSED: "Not assessed",
};

export function conclusionLabel(value: ObjectiveConclusion): string {
  return CONCLUSION_LABEL[value];
}

// --- Tables ---------------------------------------------------------------

/**
 * What a column is for, which decides the widths that show it.
 *
 * A phone is about a third of the width of the screen these tables were
 * drawn for, so a table of eight columns cannot be drawn on one — and making
 * the page scroll sideways is not an answer, it is the problem with a handle
 * on it. Instead every column says what it is for:
 *
 *   primary    what identifies the row. Always shown.
 *   status     what a person must see while scanning: MET, overdue, a
 *              severity, a result. Always shown, on every width.
 *   secondary  useful, not needed to scan: an owner, a date changed, a
 *              count. From a tablet upwards.
 *   detail     what the record itself says once it is open. Desktop only.
 *
 * Nothing is lost by hiding a column: the value stays in the record, and the
 * row opens it. What must never be hidden is a state that changes how the
 * row reads — see `docs/MOBILE.md`.
 */
export type ColumnPriority = "primary" | "status" | "secondary" | "detail";

const PRIORITY_CLASS: Record<ColumnPriority, string> = {
  primary: "",
  status: "",
  secondary: "hidden tablet:table-cell",
  detail: "hidden desktop:table-cell",
};

/** The classes that show a cell of this priority. Use it on the `td` too. */
export function columnClass(priority: ColumnPriority, own?: string): string {
  return classes(PRIORITY_CLASS[priority], own);
}

/** One column head: a label, or a label with its priority and classes. */
export type DataColumn =
  | ReactNode
  | {
      readonly label: ReactNode;
      readonly className?: string;
      readonly priority?: ColumnPriority;
    };

function columnOf(column: DataColumn): { label: ReactNode; className?: string | undefined } {
  if (typeof column === "object" && column !== null && !isValidElement(column) && "label" in column) {
    const { label, className, priority } = column;
    return {
      label,
      className: priority === undefined ? className : columnClass(priority, className),
    };
  }
  return { label: column };
}

/**
 * A table with one head row. The children are the body rows.
 *
 * The `compact` variant is the list table of the record pages. The `light`
 * variant is the small table of the executive dashboard. A `false` column is
 * left out, so a caller can show a column on a condition.
 */
export function DataTable({
  headers,
  children,
  className,
  testId,
  variant = "compact",
  cellClassName,
}: {
  readonly headers: readonly (DataColumn | false | null | undefined)[];
  readonly children: ReactNode;
  readonly className?: string;
  readonly testId?: string;
  readonly variant?: "compact" | "light";
  /** The classes of every head cell that names none of its own. */
  readonly cellClassName?: string;
}) {
  const table = variant === "light" ? "w-full text-sm" : "w-full density-compact";
  const head = variant === "light" ? "text-left text-muted" : "border-b border-divider text-left";
  return (
    // The one place a table is allowed to scroll sideways is inside its own
    // box. A table that cannot shrink any further — a long identifier, a
    // column that must not wrap — used to widen the whole document, and the
    // page scrolled away from the navigation. Now the table scrolls and the
    // page does not. Most tables never reach this: their columns are marked
    // with the width that shows them, and a phone is given record rows.
    <div className="max-w-full min-w-0 overflow-x-auto">
    <table className={classes(className, table)} data-testid={testId}>
      <thead>
        <tr className={head}>
          {headers
            .filter((column) => column !== false && column !== null && column !== undefined)
            .map((column, index) => {
              const { label, className: own } = columnOf(column);
              return (
                <th key={index} className={own ?? cellClassName}>
                  {label}
                </th>
              );
            })}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
    </div>
  );
}

// --- Tabs inside a page ---------------------------------------------------

/**
 * The row of tabs inside a page, and the class of one tab.
 *
 * A page that shows two views of one record — the controls and the history,
 * the runs and the procedure, the overview and the queue — draws this. It was
 * drawn three different ways before: an underline here, a bordered box there,
 * a pill somewhere else, and the same application looked like three.
 *
 * The underline is the one that stayed, because the areas along the top of
 * the application already mark the open one that way, so a page reads as the
 * second level of the same navigation and not as a different idea.
 *
 * The caller keeps its own control. A tab that changes a query is a link, a
 * tab that changes state is a button, and both take `tabClass`.
 */
export function TabRow({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}) {
  return (
    <nav
      aria-label={label}
      // It scrolls sideways on a narrow screen rather than wrapping into a
      // second row that pushes the page down.
      className="-mx-4 flex min-w-0 gap-1 overflow-x-auto border-b border-divider px-4 tablet:mx-0 tablet:flex-wrap tablet:px-0"
    >
      {children}
    </nav>
  );
}

/** The classes of one tab in a `TabRow`. */
export function tabClass(current: boolean, own?: string): string {
  return classes(
    "min-h-11 shrink-0 whitespace-nowrap rounded-t-arc px-3 py-2 text-sm",
    current
      ? "border-b-2 border-accent font-semibold text-accent"
      : "text-muted hover:text-ink",
    own,
  );
}

// --- Layout parts ---------------------------------------------------------

/**
 * A titled block of a page.
 *
 * On a tablet and a desktop it is a card: a border, a heading bar, paper
 * behind it. On a phone it is a heading and a hairline, and nothing else —
 * a screen of eight bordered boxes inside a bordered page reads as boxes,
 * not as work, and every border costs about a line of the little height a
 * phone has. The words, the order and the contents do not change.
 */
export function Panel({
  title,
  action,
  children,
  bodyClassName,
}: {
  readonly title?: ReactNode;
  readonly action?: ReactNode;
  readonly children: ReactNode;
  readonly bodyClassName?: string;
}) {
  return (
    <section className="min-w-0 tablet:rounded-arc tablet:border tablet:border-divider tablet:bg-paper">
      {title !== undefined && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-divider pb-1 tablet:gap-4 tablet:px-4 tablet:py-3 tablet:pb-3">
          <h2 className="text-base font-semibold">{title}</h2>
          {action}
        </header>
      )}
      <div className={classes("py-2 tablet:py-0", bodyClassName ?? "tablet:p-4")}>{children}</div>
    </section>
  );
}

export function Field({
  label,
  hint,
  children,
  id,
}: {
  readonly label: string;
  readonly hint?: string;
  readonly children: ReactNode;
  readonly id: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      {hint !== undefined && (
        <p id={`${id}-hint`} className="text-sm text-muted">
          {hint}
        </p>
      )}
      {children}
    </div>
  );
}

export const inputClass =
  "w-full rounded-arc border border-input-border bg-paper px-3 py-2 " +
  "text-ink placeholder:text-muted";

/** Show a problem that the server reported, with the correction it gave. */
/**
 * The class for a file input.
 *
 * A bare file input prints "No file chosen" beside a small grey button that
 * does not look like one, and people reported it as a missing button. This
 * styles the browser's own button to match the rest of the interface.
 */
export const FILE_INPUT_CLASS =
  "mt-1 block w-full text-sm text-muted " +
  "file:mr-3 file:rounded-arc file:border-0 file:bg-highlight file:px-4 " +
  "file:py-2 file:text-sm file:font-medium file:text-on-fill " +
  "hover:file:bg-highlight-dark file:cursor-pointer";

export function Problem({ message }: { readonly message: string }) {
  return (
    <p role="alert" className="rounded-arc border border-error bg-error-tint px-3 py-2 text-error">
      {message}
    </p>
  );
}

/** Show a short note beside the work, such as a rule or a limit. */
export function Note({ children }: { readonly children: ReactNode }) {
  return (
    <p className="rounded-arc border border-divider bg-canvas px-3 py-2 text-sm text-muted">
      {children}
    </p>
  );
}

/**
 * Something is pending.
 *
 * The sentence is the whole message: it is announced, it says what is
 * loading, and it is correct on its own. The astronaut beside it is
 * decoration and carries nothing — it is absent when artwork is switched
 * off, still when motion is switched off, and never drawn on an
 * authentication, security, assessor, or public screen.
 */
export function Loading({ what }: { readonly what: string }) {
  return (
    <p className="flex items-center gap-2 p-4 text-muted" aria-live="polite">
      <LoadingMark />
      {`Loading ${what}.`}
    </p>
  );
}

/**
 * Show a value that has no data yet, with the next useful action.
 *
 * One line and at most one action. An empty list is the least interesting
 * thing on a screen, and on a phone a bordered box holding a sentence about
 * nothing is the largest thing on it.
 */
export function Empty({
  message,
  action,
}: {
  readonly message: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-3 py-2 tablet:p-4">
      <p className="text-muted">{message}</p>
      {action}
    </div>
  );
}

/** A fixed-width identifier, such as a control number or a hash. */
export function Code({ children }: { readonly children: ReactNode }) {
  return <span className="font-mono text-sm">{children}</span>;
}
